import { PayloadAction } from '@reduxjs/toolkit';

import { HospiceIntakeDto, HospiceIntakesClient } from '@medone/medonehp-api-client';

import { handleError } from '../../../../shared/common/HandleErrors';
import { shouldSkipByPatientId } from '../../../../shared/common/helpers';
import { Axios } from '../../../../shared/common/http';
import { AppDispatch, AppThunk, RootState } from '../../../../shared/store';
import { CensusState } from './models';
import { censusSlice } from './slice';

export const reducers = {
    setHospiceIntakes: (state: CensusState, action: PayloadAction<HospiceIntakeDto[]>) => {
        if (state.patient != null) {
            state.patientHospiceIntakes = action.payload;
        }
    },

    setShowHospiceIntake: (state: CensusState, action: PayloadAction<boolean>) => {
        state.showHospiceIntake = action.payload;
    },
};

export function fetchHospiceIntakes(patientId: number): AppThunk {
    return async (dispatch: AppDispatch, getState: () => RootState) => {
        const client = new HospiceIntakesClient(null, Axios);

        try {
            const response = await client.getAll(patientId);

            // Guard against race condition: discard if user navigated to a different patient
            if (shouldSkipByPatientId(getState().census, patientId)) {
                return;
            }

            if (response.result.succeeded) {
                dispatch(censusSlice.actions.setHospiceIntakes(response.result.entity));
            }
        } catch (error) {
            handleError(error, () => {});
        }
    };
}

export function addHospiceIntake(dto: HospiceIntakeDto): AppThunk<Promise<boolean>> {
    return async (dispatch: AppDispatch, getState: () => RootState) => {
        const client = new HospiceIntakesClient(null, Axios);

        try {
            const response = await client.post(dto);

            if (response.result.succeeded) {
                await dispatch(fetchHospiceIntakes(dto.patientId));
            } else {
                handleError(response, () => {});
            }

            return response.result.succeeded;
        } catch (error) {
            handleError(error, () => {});
        }

        return false;
    };
}

export function deleteHospiceIntake(dto: HospiceIntakeDto): AppThunk<Promise<boolean>> {
    return async (dispatch: AppDispatch, getState: () => RootState) => {
        const client = new HospiceIntakesClient(null, Axios);

        try {
            const response = await client.delete(dto.id);

            if (response.result.succeeded) {
                await dispatch(fetchHospiceIntakes(dto.patientId));
            } else {
                handleError(response, () => {});
            }

            return response.result.succeeded;
        } catch (error) {
            handleError(error, () => {});
        }

        return false;
    };
}

export const selectPatientHospiceIntakes = (state: RootState) => state.census.patientHospiceIntakes;
export const selectShowHospiceIntake = (state: RootState) => state.census.showHospiceIntake;
